import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  StarIcon,
  CheckIcon,
  HeartIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { tourService } from '../services/tourService';
import { useTheme } from '../contexts/ThemeContext';
import PricingFilter from '../components/Common/PricingFilter';

const CulturalExperiences: React.FC = () => {
  const { actualTheme } = useTheme();
  const [culturalTours, setCulturalTours] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get Cultural tours from the service
    const loadTours = async () => {
      try {
        setLoading(true);
        const tours = await tourService.getToursByCategory('Cultural');
        setCulturalTours(tours);
      } catch (error) {
        console.error('Error loading Cultural tours:', error);
        setCulturalTours([]);
      } finally {
        setLoading(false);
      }
    };

    loadTours();
  }, []);

  // Listen for storage changes to update tours when admin makes changes (from other tabs only)
  useEffect(() => {
    let isHandling = false;
    const handleStorageChange = async (e: StorageEvent) => {
      // Only react to storage events from other tabs/windows, not our own
      if (e.key && (e.key.startsWith('tours_') || e.key === 'tours_cache') && !isHandling) {
        isHandling = true;
        try {
          setLoading(true);
          const tours = await tourService.getToursByCategory('Cultural');
          setCulturalTours(tours);
        } catch (error) {
          console.error('Error loading Cultural tours on storage change:', error);
          setCulturalTours([]);
        } finally {
          setLoading(false);
          setTimeout(() => { isHandling = false; }, 1000); // Prevent rapid successive calls
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // No mock data - using only real admin data

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Floating Header Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 md:pt-12">
        <div className={`rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-lg ${actualTheme === 'dark'
          ? 'bg-gradient-to-br from-primary-900 to-primary-800 border border-primary-700/50'
          : 'bg-gradient-to-br from-primary-800 to-primary-900 border border-primary-700/30'
          } text-white`}>
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Cultural Experiences
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-primary-100 max-w-2xl mx-auto leading-relaxed">
              Immerse yourself in the rich traditions and vibrant cultures of East Africa.
              Connect with local communities and experience authentic cultural exchanges.
            </p>
          </div>
        </div>
      </div>

      {/* Global Pricing Filter */}
      <PricingFilter />

      {/* Features Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Authentic Cultural Immersion</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our cultural experiences are designed to provide genuine connections with local communities,
              respecting traditions while creating meaningful exchanges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Community Led</h3>
              <p className="text-neutral-600">Experiences designed and led by local community members</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Respectful Tourism</h3>
              <p className="text-neutral-600">Cultural experiences that benefit local communities</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <StarIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Authentic Experiences</h3>
              <p className="text-neutral-600">Genuine cultural exchanges, not tourist performances</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Educational Value</h3>
              <p className="text-neutral-600">Learn about traditions, history, and modern life</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cultural Tours Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary-900 mb-4">Featured Cultural Experiences</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Choose from our carefully selected cultural experiences that offer authentic
              connections with local communities and traditions.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-800"></div>
              <span className="ml-4 text-xl text-neutral-600">Loading cultural experiences...</span>
            </div>
          ) : culturalTours.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {culturalTours.map((tour) => (
                <div key={tour.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  {/* Tour Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={tour.images && tour.images.length > 0 ? tour.images[0] : 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'}
                      alt={tour.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary-800 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Cultural
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <div className="bg-white rounded-full p-2 shadow-sm">
                        <StarSolidIcon className="h-4 w-4 text-yellow-400" />
                        <span className="text-sm font-medium text-gray-900 ml-1">
                          {tour.rating}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tour Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-primary-900 mb-2">
                      {tour.title}
                    </h3>
                    <p className="text-neutral-600 mb-4 line-clamp-3">
                      {tour.description}
                    </p>

                    {/* Tour Details */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-neutral-600">
                        <MapPinIcon className="h-4 w-4 mr-2 text-primary-600" />
                        <span className="text-sm">{tour.location?.name || (typeof tour.location === 'string' ? tour.location : 'Location not specified')}</span>
                      </div>
                      <div className="flex items-center text-neutral-600">
                        <ClockIcon className="h-4 w-4 mr-2 text-primary-600" />
                        <span className="text-sm">{tour.duration}</span>
                      </div>
                      <div className="flex items-center text-neutral-600">
                        <UsersIcon className="h-4 w-4 mr-2 text-primary-600" />
                        <span className="text-sm">Max {tour.maxParticipants} people</span>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {tour.highlights.slice(0, 3).map((highlight: string, index: number) => (
                          <span
                            key={index}
                            className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Price and CTA */}
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-primary-800">
                          ${tour.price.toLocaleString()}
                        </span>
                        <span className="text-neutral-600 text-sm ml-1">per person</span>
                      </div>
                      <Link
                        to={`/tours/${tour.id}`}
                        className="bg-primary-800 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-neutral-500 text-lg mb-4">
                No Cultural experiences available at the moment.
              </div>
              <p className="text-neutral-400 mb-6">
                Check back soon for new cultural experiences, or explore our other tour categories.
              </p>
              <Link
                to="/tours"
                className="inline-flex items-center bg-primary-800 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                View All Tours
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Cultural Values Section */}
      <div className="py-16 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-primary-900 mb-4">Our Cultural Values</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We believe in responsible cultural tourism that benefits local communities
              and preserves traditions for future generations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Community Benefit</h3>
              <p className="text-neutral-600">A portion of every cultural experience fee goes directly to the local community</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Cultural Respect</h3>
              <p className="text-neutral-600">We work with communities to ensure experiences are authentic and respectful</p>
            </div>
            <div className="text-center">
              <div className="bg-primary-800 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold text-primary-900 mb-2">Educational Impact</h3>
              <p className="text-neutral-600">Learn about traditions, history, and the challenges facing modern communities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-16 bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready for Cultural Immersion?</h2>
          <p className="text-xl text-neutral-200 mb-8 max-w-3xl mx-auto">
            Connect with local communities and experience authentic cultural exchanges.
            Book your cultural experience today and create meaningful connections.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/tours"
              className="bg-white text-primary-800 hover:bg-neutral-100 px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              View All Tours
            </Link>
            <Link
              to="/contact"
              className="bg-primary-700 hover:bg-primary-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CulturalExperiences;

